import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { getClinicPortalServiceTypeHeader } from '@/hooks/main/clinicPortalServiceType';

// ==================== Types ====================

export interface LongTermFrequentItem {
  id: number;
  patient_id: number;
  clinic_id: number;
  visit_yearmonth: number | string;
  massager_id?: number | null;
  valuation_date?: string;
  // A2 only
  degree_of_pain?: number;
  // R2 only
  valuation_betray_type_id?: number;
  valuation_sit_up_type_id?: number;
  valuation_sitting_type_id?: number;
  valuation_stand_up_type_id?: number;
  valuation_standing_type_id?: number;
  // Common
  recovery_type_id?: number;
  treatment_continuation_reason?: string;
  create_document_date?: string;
  visit_count?: number;
  massager_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface LongTermFrequentListQueryParams {
  page?: number;
  per_page?: number;
  sort_type?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface LongTermFrequentListResponse {
  success: boolean;
  data: {
    patient: {
      id: number;
      serial_number: number | string;
      current_patient_information?: { name?: string; name_kana?: string };
    };
    long_term_frequents: LongTermFrequentItem[];
    total_count?: number;
    pagination?: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  };
}

export interface LongTermFrequentPayload {
  visit_yearmonth: string;
  massager_id?: number | null;
  valuation_date?: string;
  // A2 only
  degree_of_pain?: number;
  // R2 only
  valuation_betray_type_id?: number;
  valuation_sit_up_type_id?: number;
  valuation_sitting_type_id?: number;
  valuation_stand_up_type_id?: number;
  valuation_standing_type_id?: number;
  // Common
  recovery_type_id?: number;
  treatment_continuation_reason?: string;
  create_document_date?: string;
}

export interface LongTermFrequentDetailResponse {
  success: boolean;
  message?: string;
  data: LongTermFrequentItem;
}

export interface MonthDataResponse {
  success: boolean;
  data: {
    // Visits & massager
    visit_count: number;
    massager_name: string;
    massager_id?: number | null;
    default_massager_id?: number | null;
    massagers?: { id: number; name: string }[];
    has_consent?: boolean;
    // Consent — A2
    disease_name: string[];
    disease_name_other?: string;
    treatment_content: string[];
    first_treatment_date?: string | null;
    // Consent — R2
    symptom_muscle?: string | null;
    symptom_joint?: string | null;
    symptom_other?: string | null;
    massage?: string | null;
    orthomorphia?: string | null;
    // Prev month evaluation
    is_prev: boolean;
    prev_recovery_type_name: string;
    prev_treatment_continuation_reason: string;
    prev_recovery_type_id: number | null;
    // Prev month — R2
    prev_valuation_betray_type_id?: number;
    prev_valuation_sit_up_type_id?: number;
    prev_valuation_sitting_type_id?: number;
    prev_valuation_stand_up_type_id?: number;
    prev_valuation_standing_type_id?: number;
  };
}

export interface LastVisitDateResponse {
  success: boolean;
  data: {
    last_visit_date: string | null;
  };
}

export interface MasterDataItem {
  id: number;
  name: string;
  value: string | null;
  sortno: number;
}

export interface MasterDataResponse {
  success: boolean;
  data: {
    recovery_types: MasterDataItem[];
    valuation_types: MasterDataItem[];
  };
}

// ==================== Helper ====================

function serviceTypeHeaders(): Record<string, string> {
  return { 'X-Service-Type': getClinicPortalServiceTypeHeader() };
}

// ==================== API Functions ====================

export async function fetchLongTermFrequentList(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  params?: LongTermFrequentListQueryParams
): Promise<LongTermFrequentListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', String(params.page));
  if (params?.per_page) queryParams.append('per_page', String(params.per_page));
  if (params?.sort_type) {
    queryParams.append('sortBy', params.sort_type);
    queryParams.append('sortOrder', params.sort_dir === 'desc' ? 'desc' : 'asc');
  }

  const response = await apiClient.get<LongTermFrequentListResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/long-term-frequents`) +
      (queryParams.toString() ? `?${queryParams.toString()}` : ''),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function fetchLongTermFrequentOne(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: string
): Promise<LongTermFrequentItem> {
  const response = await apiClient.get<LongTermFrequentDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/long-term-frequents/${id}`),
    { headers: serviceTypeHeaders() }
  );
  return response.data.data;
}

export async function createLongTermFrequent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  payload: LongTermFrequentPayload
): Promise<LongTermFrequentDetailResponse> {
  const response = await apiClient.post<LongTermFrequentDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/long-term-frequents`),
    payload,
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function updateLongTermFrequent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number,
  payload: LongTermFrequentPayload
): Promise<LongTermFrequentDetailResponse> {
  const response = await apiClient.put<LongTermFrequentDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/long-term-frequents/${id}`),
    payload,
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function deleteLongTermFrequent(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number
): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.delete(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/long-term-frequents/${id}`),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function fetchMonthData(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  visitYearmonth: string
): Promise<MonthDataResponse> {
  const response = await apiClient.get<MonthDataResponse>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/long-term-frequents/month-data?visit_yearmonth=${visitYearmonth}`
    ),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function fetchLastVisitDate(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  visitYearmonth: string
): Promise<LastVisitDateResponse> {
  const response = await apiClient.get<LastVisitDateResponse>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/long-term-frequents/last-visit-date?visit_yearmonth=${visitYearmonth}`
    ),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

export async function fetchMasterData(
  siteType: string,
  clinicNumber: string,
  patientId: string
): Promise<MasterDataResponse> {
  const response = await apiClient.get<MasterDataResponse>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/long-term-frequents/master-data`
    ),
    { headers: serviceTypeHeaders() }
  );
  return response.data;
}

/** Generate PDF for a single long-term frequent record. Returns blob. */
export async function fetchLongTermFrequentPdf(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  id: number
): Promise<Blob> {
  const response = await apiClient.get(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/long-term-frequents/${id}/pdf`
    ),
    { headers: serviceTypeHeaders(), responseType: 'blob' }
  );
  return response.data;
}
